import { prefetchRoute } from "./prefetchRoute";

/**
 * Idle-mounted IntersectionObserver that prefetches route chunks for any
 * internal <a href="/..."> as soon as it scrolls into view. Combined with
 * the existing hover prefetch in Header, this makes most clicks effectively
 * instant — by the time the user clicks, the chunk is already in cache.
 *
 * Performance safety:
 *  - Only attached after `requestIdleCallback`, so it never competes with
 *    LCP / hydration work on first paint.
 *  - Each link is unobserved after the first hit (no repeated work).
 *  - `prefetchRoute` itself dedupes per session, so re-scanning is cheap.
 *  - A MutationObserver picks up links added to the DOM later (route
 *    transitions, data loads) without re-scanning the whole tree.
 */

let started = false;
let io: IntersectionObserver | null = null;
let mo: MutationObserver | null = null;

const isInternalLink = (a: HTMLAnchorElement): boolean => {
  const href = a.getAttribute("href");
  if (!href) return false;
  if (a.dataset.noPrefetch !== undefined) return false;
  if (a.target === "_blank") return false;
  if (!href.startsWith("/")) return false;
  // Skip admin (route chunk isn't in prefetchRoute map) and anchors
  if (href.startsWith("/admin")) return false;
  if (href.startsWith("/#")) return false;
  return true;
};

const observeLinks = (root: ParentNode) => {
  if (!io) return;
  const links = root.querySelectorAll<HTMLAnchorElement>('a[href^="/"]');
  links.forEach((a) => {
    if (isInternalLink(a)) io!.observe(a);
  });
};

export function startLinkPrefetcher() {
  if (started || typeof window === "undefined") return;
  if (typeof IntersectionObserver === "undefined") return;
  started = true;

  const schedule =
    (window as Window & { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));

  schedule(() => {
    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const a = entry.target as HTMLAnchorElement;
          const href = a.getAttribute("href");
          if (href) prefetchRoute(href);
          io!.unobserve(a);
        });
      },
      { rootMargin: "200px" }
    );

    observeLinks(document);

    mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          const el = n as Element;
          if (el.tagName === "A" && isInternalLink(el as HTMLAnchorElement)) {
            io!.observe(el);
          } else {
            observeLinks(el);
          }
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  });
}
